import { withKeys } from "@/common/helpers/list-keys";
import { cn } from "@/common/helpers/utils";
import { useDevice } from "@/common/hooks/use-device";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { MoreHorizontalIcon } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonSize } from "./button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu";

export const RowActionVariant = {
  Default: ButtonColorVariant.Primary,
  Success: ButtonColorVariant.Success,
  Warning: ButtonColorVariant.Warning,
  Error: ButtonColorVariant.Error,
} as const satisfies Record<string, ButtonColorVariant>;

export interface RowAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: ButtonColorVariant;
  disabled?: boolean;
  needsConfirm?: boolean;
  confirmLabels?: {
    title: string;
    text?: string;
    confirmButton?: string;
    cancelButton?: string;
  };
  confirmCustomComponent?: ReactNode;
}

interface TableRowActionsProps {
  actions: RowAction[];
  isExpanded?: boolean;
  className?: string;
}

export function TableRowActions({ actions, isExpanded, className }: TableRowActionsProps) {
  const { t } = useTranslation(["common"]);
  const { isMobileOrTablet } = useDevice();

  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<RowAction | null>(null);
  const clearPendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClearPendingTimeout = () => {
    if (clearPendingTimeoutRef.current) {
      clearTimeout(clearPendingTimeoutRef.current);
      clearPendingTimeoutRef.current = null;
    }
  };

  const clearPendingAfterAnimation = () => {
    cancelClearPendingTimeout();
    clearPendingTimeoutRef.current = setTimeout(() => setPendingAction(null), 300);
  };

  useEffect(() => {
    return () => {
      cancelClearPendingTimeout();
    };
  }, []);

  useEffect(() => {
    if (confirmOpen) cancelClearPendingTimeout();
  }, [confirmOpen]);

  const runAction = (action: RowAction) => {
    action.onClick();
  };

  if (isMobileOrTablet && isExpanded) return null;

  return (
    <div className={cn("flex items-center justify-end", className)}>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={ButtonVariant.Ghost}
            colorVariant={ButtonColorVariant.Secondary}
            size={ButtonSize.Sm}
            className="h-8 w-8 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">{t("ram:OpenMenu")}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {withKeys(actions, (action, index) => `${action.label}-${index}`).map(({ key, item: action }) => (
            <DropdownMenuItem
              key={key}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (action.disabled) return;

                setMenuOpen(false);
                if (action.needsConfirm) {
                  cancelClearPendingTimeout();
                  setPendingAction(action);
                  setConfirmOpen(true);
                } else {
                  runAction(action);
                }
              }}
              disabled={action.disabled}
              className={cn(action.variant === RowActionVariant.Error && "text-error focus:text-error")}
            >
              {action.icon}
              <span className="ml-2">{action.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <BaseConfirmDialog
        open={confirmOpen}
        title={pendingAction?.confirmLabels?.title ?? pendingAction?.label ?? t("common:ConfirmAction")}
        description={pendingAction?.confirmLabels?.text}
        customComponent={pendingAction?.confirmCustomComponent}
        labelCancel={pendingAction?.confirmLabels?.cancelButton ?? t("common:Cancel")}
        labelConfirm={pendingAction?.confirmLabels?.confirmButton ?? t("common:Confirm")}
        cancelVariant={ButtonVariant.Outlined}
        confirmColorVariant={
          pendingAction?.variant === RowActionVariant.Error ? ButtonColorVariant.Error : ButtonColorVariant.Primary
        }
        onCancel={() => {
          setConfirmOpen(false);
          clearPendingAfterAnimation();
        }}
        onConfirm={() => {
          if (pendingAction !== null) {
            const action = pendingAction;
            setConfirmOpen(false);
            clearPendingAfterAnimation();
            runAction(action);
          }
        }}
      />
    </div>
  );
}
